import * as d3 from 'd3';
import { throttle } from 'lodash';
import { MouseEventHandler, useCallback, useMemo, useRef } from 'react';

import { CursorPosition, useCursorUpdate } from './Cursor';

/**
 * Helper to use when subscribing to mouse events for the purposes of chart cursors.
 * The returned mouse listener functions update the cursor state based on the position of the mouse.
 * @param xyToCursorPosition A function to translate the coordinate position
 * into the domain of the cursor (either the time domain or latency bucket domain).
 * @returns onMouseLeave & onMouseMove event listeners.
 */
export function useTimeCursorHandlers(
  xyToCursorPosition: (xy: [number, number]) => CursorPosition,
) {
  const xyToCursorPositionRef = useRef(xyToCursorPosition);
  xyToCursorPositionRef.current = xyToCursorPosition;
  const onCursorChange = useCursorUpdate();
  const throttledOnCursorChange = useMemo(
    () => throttle(onCursorChange, 40),
    [onCursorChange],
  );
  const onMouseLeave = useCallback(() => {
    throttledOnCursorChange.cancel();
    onCursorChange(null);
  }, [onCursorChange, throttledOnCursorChange]);
  const onMouseMove = useCallback<MouseEventHandler<SVGSVGElement>>(
    (event) => {
      throttledOnCursorChange(
        xyToCursorPositionRef.current(
          d3.clientPoint(event.currentTarget, event),
        ),
      );
    },
    [throttledOnCursorChange],
  );
  return { onMouseLeave, onMouseMove };
}
